/**
 * First-run empty state (TASK-630).
 *
 * Renders on the Today dashboard only while the account has zero clients
 * ([[feedback_one_attention_surface]] — a dashboard state, not a new route).
 * Admin actions: create the first client inline, or jump to the /onboarding
 * import wizard. Invited (non-admin) users see the explainer without actions.
 * Self-contained: fetches its own count, renders null once a client exists.
 */

import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { UserPlus, Upload, Sparkles, X } from 'lucide-react'
import { httpClient } from '../../lib/httpClient'
import { useAccount } from '../../contexts/AccountContext'
import { OnboardingProvider } from '../../contexts/OnboardingContext'
import { ClientCreateForm } from '../clients/ClientCreateForm'

const FirstRunCard: React.FC = () => {
  const navigate = useNavigate()
  const { isAdmin } = useAccount()
  const [hasClients, setHasClients] = useState<boolean | null>(null)
  const [showCreateForm, setShowCreateForm] = useState(false)

  useEffect(() => {
    let cancelled = false
    // Direct httpClient probe: clientService.getAll swallows errors into an empty
    // list, which would show this card to a populated tenant on a transient failure.
    httpClient
      .get<{ clients: unknown[] }>('/api/clients?limit=1')
      .then((res) => {
        if (!cancelled) setHasClients((res.clients?.length ?? 0) > 0)
      })
      .catch(() => {
        // On error, assume a populated account — never block the dashboard.
        if (!cancelled) setHasClients(true)
      })
    return () => {
      cancelled = true
    }
  }, [])

  // Loading or account already has clients → nothing to show.
  if (hasClients !== false) return null

  return (
    <div className="mx-4 mt-4 rounded-xl border border-kielis-gold/40 bg-gradient-to-r from-amber-50 to-white p-6 shadow-sm sm:mx-6 lg:mx-8">
      <div className="flex items-start gap-4">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-amber-100">
          <Sparkles className="h-5 w-5 text-kielis-gold" />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="text-base font-semibold text-gray-900">Welcome to Kielis</h3>
          {isAdmin ? (
            <>
              <p className="mt-1 text-sm text-gray-600">
                Your workspace is ready but empty. Add your first client, or import your
                existing book (email, Excel, PDFs) with the guided wizard.
              </p>
              <div className="mt-4 flex flex-wrap gap-3">
                <button
                  onClick={() => setShowCreateForm(true)}
                  className="inline-flex items-center rounded-md bg-kielis-navy px-4 py-2 text-sm font-medium text-white hover:bg-opacity-90"
                >
                  <UserPlus className="mr-2 h-4 w-4" />
                  Add your first client
                </button>
                <button
                  onClick={() => navigate('/onboarding')}
                  className="inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  <Upload className="mr-2 h-4 w-4" />
                  Import your data
                </button>
              </div>
            </>
          ) : (
            <p className="mt-1 text-sm text-gray-600">
              No clients yet — your administrator is still setting up this workspace.
            </p>
          )}
        </div>
      </div>

      {/* Inline create modal (ClientCreateForm needs OnboardingProvider) */}
      {showCreateForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowCreateForm(false)} />
          <div className="relative w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Add your first client</h3>
              <button
                onClick={() => setShowCreateForm(false)}
                className="text-gray-400 hover:text-gray-600"
                aria-label="Close"
              >
                <X size={20} />
              </button>
            </div>
            <OnboardingProvider>
              <ClientCreateForm
                onClientCreated={() => {
                  setShowCreateForm(false)
                  setHasClients(true)
                }}
              />
            </OnboardingProvider>
          </div>
        </div>
      )}
    </div>
  )
}

export default FirstRunCard
